import type { AxiosInstance } from "axios";
import { ChevronDown, TriangleAlert, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { cn } from "../lib/cn.js";
import { fetchTrackingStores, stateMessage, TRACKING_CHANGED_EVENT, type StoreTracking } from "../lib/tracking.js";

/**
 * THE WARNING ALONG THE TOP (ticket "Plugin Control", part 3): a store whose tracking is off,
 * paused, removed from Shopify, never started or stopped, named on every page until it is fixed.
 *
 * Incident I5 is why it exists at all — Sudathi sent nothing for a month while every screen
 * showed smaller numbers without saying why. A health page nobody opens could not have caught it;
 * a line at the top of whatever they are reading would have.
 *
 * ONE LINE PER STORE, so an agency sees exactly which of its stores is affected; the first few
 * are shown and the rest fold behind "Show all". A store tracking normally — or one whose state
 * could not be read — is never listed: a banner that cries wolf is a banner people stop reading.
 *
 * ONLY A REMOVAL CAN BE DISMISSED. Every other state is something to fix, and stays until it is;
 * an uninstall may be deliberate, so once acknowledged it is hidden on this browser.
 */
const REFRESH_MS = 5 * 60_000;
const SHOWN_BEFORE_FOLD = 3;
const DISMISSED_KEY = "ecomally.trackingBanner.dismissed";

function readDismissed(): ReadonlySet<string> {
  try {
    const raw = window.localStorage.getItem(DISMISSED_KEY);
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : []);
  } catch (error) {
    // Storage unavailable (private window, blocked site data): nothing is remembered, which only
    // means a dismissed removal shows again.
    void error;
    return new Set();
  }
}

function writeDismissed(keys: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY, JSON.stringify([...keys]));
  } catch (error) {
    void error;
  }
}

/** A dismissal is for THIS removal: reinstalled and removed again, it shows again. */
const dismissKey = (store: StoreTracking): string => `${store.storeId}:${store.since ?? ""}`;

export function TrackingBanner({
  client,
  onOpen,
}: {
  readonly client: AxiosInstance;
  /** Take the member to where the store's problem is fixed. */
  readonly onOpen: (store: StoreTracking) => void;
}): React.JSX.Element | null {
  const [stores, setStores] = useState<readonly StoreTracking[]>([]);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(readDismissed);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      setStores(await fetchTrackingStores(client));
    } catch (error) {
      // Fully handled: a banner that cannot load says nothing rather than something wrong. The
      // Plugin tab and Store Health still report the store's state on their own.
      void error;
    }
  }, [client]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    const onChange = (): void => void load();
    window.addEventListener(TRACKING_CHANGED_EVENT, onChange);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(TRACKING_CHANGED_EVENT, onChange);
    };
  }, [load]);

  const affected = stores.flatMap((store) => {
    const message = stateMessage(store);
    if (message === null) return [];
    if (store.state === "uninstalled" && dismissed.has(dismissKey(store))) return [];
    return [{ store, message }];
  });
  if (affected.length === 0) return null;

  const red = affected.some((item) => item.message.tone === "red");
  const shown = expanded ? affected : affected.slice(0, SHOWN_BEFORE_FOLD);
  const hidden = affected.length - shown.length;

  return (
    <section
      aria-label="Tracking warnings"
      data-testid="tracking-banner"
      className={cn(
        "mx-4 mt-3 mb-1 rounded-lg border text-sm md:mx-6",
        red
          ? "border-red-200 bg-red-50 text-red-950 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100"
          : "border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100",
      )}
    >
      <ul className="divide-y divide-current/10">
        {shown.map(({ store, message }) => (
          <li key={store.storeId} className="flex items-start gap-3 px-4 py-2.5">
            <TriangleAlert
              aria-hidden
              className={cn("mt-0.5 size-4 shrink-0", message.tone === "red" ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400")}
            />
            <p className="min-w-0 flex-1">
              <span className="font-semibold">{store.displayName}</span>
              <span className="font-medium"> — {message.title}.</span> <span className="opacity-90">{message.body}</span>
            </p>
            <button
              type="button"
              onClick={() => onOpen(store)}
              className="shrink-0 cursor-pointer font-medium whitespace-nowrap underline underline-offset-4"
            >
              {store.state === "uninstalled" ? "Reconnect" : store.state === "not_started" ? "Finish setup" : "View"}
            </button>
            {store.state === "uninstalled" && (
              <button
                type="button"
                aria-label={`Dismiss the warning for ${store.displayName}`}
                onClick={() => {
                  const next = new Set(dismissed).add(dismissKey(store));
                  setDismissed(next);
                  writeDismissed(next);
                }}
                className="-mr-1 shrink-0 cursor-pointer rounded p-0.5 opacity-70 hover:opacity-100"
              >
                <X className="size-4" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {(hidden > 0 || (expanded && affected.length > SHOWN_BEFORE_FOLD)) && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex w-full cursor-pointer items-center justify-center gap-1 border-t border-current/10 px-4 py-1.5 text-xs font-medium"
        >
          {expanded ? "Show fewer" : `Show ${String(hidden)} more ${hidden === 1 ? "store" : "stores"}`}
          <ChevronDown className={cn("size-3.5 transition-transform", expanded && "rotate-180")} />
        </button>
      )}
    </section>
  );
}
